import { useEffect } from 'react';
import { DistanceUnit, Language, PaceOrSpeedMode, ThemeColor, ThemeMode } from '../types';
import { isDarkModeActive } from '../utils/theme';
import { oneOf } from '../utils/storage';
import { t } from '../utils/i18n';
import { usePersistentState } from './usePersistentState';

const THEME_COLORS = ['violet', 'emerald', 'cyan', 'amber', 'rose', 'slate'] as const;

/** Alle bewaarde voorkeuren, plus het toepassen van thema (licht/donker en kleur) op de pagina. */
export function useSettings() {
  const [language, setLanguage] = usePersistentState<Language>('pace_language', 'nl', oneOf(['nl', 'en']));
  const [distanceUnit, setDistanceUnit] = usePersistentState<DistanceUnit>('pace_distance_unit', 'km', oneOf(['km', 'mi']));
  const [paceOrSpeedMode, setPaceOrSpeedMode] = usePersistentState<PaceOrSpeedMode>('pace_speed_mode', 'pace', oneOf(['pace', 'speed']));
  const [themeMode, setThemeMode] = usePersistentState<ThemeMode>('pace_theme_mode', 'auto', oneOf(['light', 'dark', 'auto']));
  const [themeColor, setThemeColor] = usePersistentState<ThemeColor>('pace_theme_color', 'violet', oneOf(THEME_COLORS));

  // Licht/donker. In 'auto' volgen we het systeem én de klok (20:00-07:00), ook terwijl de app open staat.
  useEffect(() => {
    const updateTheme = () => {
      const active = isDarkModeActive(themeMode);
      document.documentElement.classList.toggle('dark', active);
      document.body.classList.toggle('dark', active);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', active ? '#0F172A' : '#F8F9FC');
    };

    updateTheme();
    if (themeMode !== 'auto') return;

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    mediaQuery?.addEventListener('change', updateTheme);
    const interval = window.setInterval(updateTheme, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') updateTheme();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mediaQuery?.removeEventListener('change', updateTheme);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [themeMode]);

  // Taal van de pagina: html lang, titel en omschrijving (zichtbaar voor schermlezers, tabbladen en zoekmachines)
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t(language, 'metaTitle');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(language, 'metaDescription'));
  }, [language]);

  // Themakleur
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeColor);
    root.classList.remove(...THEME_COLORS.map((c) => `theme-${c}`));
    root.classList.add(`theme-${themeColor}`);
  }, [themeColor]);

  return {
    language, setLanguage,
    distanceUnit, setDistanceUnit,
    paceOrSpeedMode, setPaceOrSpeedMode,
    themeMode, setThemeMode,
    themeColor, setThemeColor,
  };
}
